/**
 * UniversalLayout 路由配置 入口
 * @author LiQingSong
 */

import { lazy } from 'react';
import { IRouter, IFrontPermissionNode } from '@/@types/router.d';
import { FunctionListModule } from '@/utils/router';

/**
 * 父级列表页 → 其二级（详情/编辑）页面路径的显式映射。
 * 部分二级页在 routes.ts 中与列表页平级（如 /products/props/propValues 与
 * /products/props 是平级兄弟，并非父子嵌套），用显式数组维护父子关系
 * 更可靠，也便于后续扩展。
 */
const PARENT_SECONDARY_MAP: Record<string, string[]> = {
  '/home': [],
  '/order/order-list': ['/order/detail'],
  '/activities/coupon-list': [
    '/activities/editVenue',
    '/activities/edit',
  ],
  '/products/products-list': ['/products/edit', '/products/relateProduct'],
  '/products/category': ['/products/category/edit',],
  '/products/props': ['/products/props/propValues'],
  '/products/CustomTemplate': ['/products/CustomTemplate/edit'],
  '/products/categoryAttributes': ['/products/categoryAttributes/valueAttribute'],
  '/products/collection': [
    '/products/collection/edit',
    // '/products/collection/editProduct',
    '/products/relateProduct',
  ],
  '/contents/topic': ['/contents/topic/edit'],
  '/contents/posts': ['/contents/posts/edit', '/products/relateProduct'],
  '/mallConfig/home-list': ['/mallConfig/home/edit'],
  '/mallConfig/guidingstar': [],
  '/usercenter/user-list': [],
  '/usercenter/contact-list': [],
  '/accountCenter/account': ['/accountCenter/account/edit', '/accountCenter/account/detail'],
  '/accountCenter/role': ['/accountCenter/role/edit', '/accountCenter/role/detail'],
  '/supplyChain/factory': ['/supplyChain/factory/edit', '/supplyChain/factory/detail'],
};

/**
 * 前端菜单权限树（后端返回结构）。
 * 字段说明（使用方主要关心 front 相关字段）：
 *   - frontPath: 前端路由路径（一级/二级/三级菜单）
 *   - frontTitle: 前端菜单标题
 *   - frontRelationPermission: 关联权限编码（对应子项 permissionList 的 frontCode），无则 null
 *   - level: 1=一级模块 / 2=二级列表页 / 3=三级子页
 *   - permissionList[].frontCode / frontName: 前端权限编码 / 名称
 * permissionList[].backCode / backDesc 为后端编码与描述，供接口联调使用。
 */
const FrontPermissionTree: IFrontPermissionNode[] = [
  {
    id: '2083106383137595392',
    createDate: 1785486238539,
    updateDate: 1785486238539,
    del: false,
    frontPath: '/order',
    frontTitle: '订单管理',
    frontRelationPermission: null,
    level: 1,
    version: 2,
    publishDate: 1785486238533,
    permissionList: [],
    children: [
      {
        id: null,
        createDate: null,
        updateDate: null,
        del: false,
        frontPath: '/order/order-list',
        frontTitle: '订单列表',
        frontRelationPermission: null,
        level: 2,
        version: 2,
        publishDate: 1785486238533,
        permissionList: [
          { backCode: 'PERMISSION_ORDER_MANAGEMENT.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
          { backCode: 'PERMISSION_ORDER_MANAGEMENT.DETAIL', backDesc: '详情', frontCode: 'detail', frontName: '详情' },
          { backCode: 'PERMISSION_ORDER_MANAGEMENT.EDIT', backDesc: '编辑（修改收货地址，设置标签）', frontCode: 'edit', frontName: '编辑（修改收货地址，设置标签）' },
          { backCode: 'PERMISSION_ORDER_MANAGEMENT.REVIEW', backDesc: '审核', frontCode: 'review', frontName: '审核' },
          { backCode: 'PERMISSION_ORDER_MANAGEMENT.PRINT_MANAGE', backDesc: '印制稿管理（下载客户设计；上传生产稿）', frontCode: 'printManage', frontName: '印制稿管理（下载客户设计；上传生产稿）' },
          { backCode: 'PERMISSION_ORDER_MANAGEMENT.DISCOUNT', backDesc: '订单优惠（设置优惠）', frontCode: 'discount', frontName: '订单优惠（设置优惠）' },
          { backCode: 'PERMISSION_ORDER_MANAGEMENT.ORDER_LINK', backDesc: '订单链接（结算链接）', frontCode: 'orderLink', frontName: '订单链接（结算链接）' },
        ],
        children: [],
      },
    ],
  },
  {
    id: '2083106383137595393',
    createDate: 1785486238642,
    updateDate: 1785486238642,
    del: false,
    frontPath: '/activities',
    frontTitle: '活动管理',
    frontRelationPermission: null,
    level: 1,
    version: 2,
    publishDate: 1785486238533,
    permissionList: [],
    children: [
      {
        id: null,
        createDate: null,
        updateDate: null,
        del: false,
        frontPath: '/activities/coupon-list',
        frontTitle: '优惠券列表',
        frontRelationPermission: null,
        level: 2,
        version: 2,
        publishDate: 1785486238533,
        permissionList: [
          { backCode: 'PERMISSION_ACTIVITY_COUPON.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
          { backCode: 'PERMISSION_ACTIVITY_COUPON.DETAIL', backDesc: '详情（含查看会场）', frontCode: 'detail', frontName: '详情（含查看会场）' },
          { backCode: 'PERMISSION_ACTIVITY_COUPON.EDIT', backDesc: '编辑（含会场配置）', frontCode: 'edit', frontName: '编辑（含会场配置）' },
          { backCode: 'PERMISSION_ACTIVITY_COUPON.ENABLE', backDesc: '取消', frontCode: 'enable', frontName: '取消' },
        ],
        children: [],
      },
    ],
  },
  {
    id: '2083106383137595394',
    createDate: 1785486238642,
    updateDate: 1785486238642,
    del: false,
    frontPath: '/products',
    frontTitle: '商品管理',
    frontRelationPermission: null,
    level: 1,
    version: 2,
    publishDate: 1785486238533,
    permissionList: [],
    children: [
      {
        id: null,
        createDate: null,
        updateDate: null,
        del: false,
        frontPath: '/products/products-list',
        frontTitle: '商品管理',
        frontRelationPermission: null,
        level: 2,
        version: 2,
        publishDate: 1785486238533,
        permissionList: [
          { backCode: 'PERMISSION_PRODUCT_MANAGEMENT.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
          { backCode: 'PERMISSION_PRODUCT_MANAGEMENT.EDIT', backDesc: '编辑', frontCode: 'edit', frontName: '编辑' },
          { backCode: 'PERMISSION_PRODUCT_MANAGEMENT.PUBLISH', backDesc: '上/下架', frontCode: 'publish', frontName: '上/下架' },
          { backCode: 'PERMISSION_PRODUCT_MANAGEMENT.SYN_PRODUCTS', backDesc: '同步商品', frontCode: 'synProducts', frontName: '同步商品' },
          { backCode: 'PERMISSION_PRODUCT_MANAGEMENT.RELATE_PRODUCT', backDesc: '关联商品', frontCode: 'relateProduct', frontName: '关联商品' },
        ],
        children: [
          {
            id: null,
            createDate: null,
            updateDate: null,
            del: false,
            frontPath: '/products/products/relateProduct',
            frontTitle: '样品-关联商品',
            frontRelationPermission: 'relateProduct',
            level: 3,
            version: 2,
            publishDate: 1785486238533,
            permissionList: [
              { backCode: 'PERMISSION_PRODUCT_RELATE_PRODUCT.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
              { backCode: 'PERMISSION_PRODUCT_RELATE_PRODUCT.EDIT', backDesc: '编辑', frontCode: 'edit', frontName: '编辑' },
              { backCode: 'PERMISSION_PRODUCT_RELATE_PRODUCT.DELETE', backDesc: '删除', frontCode: 'delete', frontName: '删除' },
            ],
            children: [],
          },
        ],
      },
      {
        id: null,
        createDate: null,
        updateDate: null,
        del: false,
        frontPath: '/products/category',
        frontTitle: '分类列表',
        frontRelationPermission: null,
        level: 2,
        version: 2,
        publishDate: 1785486238533,
        permissionList: [
          { backCode: 'PERMISSION_PRODUCT_CATALOG.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
          { backCode: 'PERMISSION_PRODUCT_CATALOG.EDIT', backDesc: '编辑', frontCode: 'edit', frontName: '编辑' },
          { backCode: 'PERMISSION_PRODUCT_CATALOG.DELETE', backDesc: '删除', frontCode: 'delete', frontName: '删除' },
          { backCode: 'PERMISSION_PRODUCT_CATALOG.ADD_CATEGORY', backDesc: '添加分类', frontCode: 'addCategory', frontName: '添加分类' },
        ],
        children: [],
      },
      {
        id: null,
        createDate: null,
        updateDate: null,
        del: false,
        frontPath: '/products/props',
        frontTitle: '属性列表',
        frontRelationPermission: null,
        level: 2,
        version: 2,
        publishDate: 1785486238533,
        permissionList: [
          { backCode: 'PERMISSION_PRODUCT_PROPERTY.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
          { backCode: 'PERMISSION_PRODUCT_PROPERTY.EDIT', backDesc: '编辑', frontCode: 'edit', frontName: '编辑' },
          { backCode: 'PERMISSION_PRODUCT_PROPERTY.DELETE', backDesc: '删除', frontCode: 'delete', frontName: '删除' },
          { backCode: 'PERMISSION_PRODUCT_PROPERTY.VALUE_MANAGE', backDesc: '值管理', frontCode: 'valueManage', frontName: '值管理' },
        ],
        children: [
          {
            id: null,
            createDate: null,
            updateDate: null,
            del: false,
            frontPath: '/products/props/propValues',
            frontTitle: '属性列表-值管理',
            frontRelationPermission: 'valueManage',
            level: 3,
            version: 2,
            publishDate: 1785486238533,
            permissionList: [
              { backCode: 'PERMISSION_PRODUCT_PROPERTY_VALUE.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
              { backCode: 'PERMISSION_PRODUCT_PROPERTY_VALUE.EDIT', backDesc: '编辑', frontCode: 'edit', frontName: '编辑' },
              { backCode: 'PERMISSION_PRODUCT_PROPERTY_VALUE.DELETE', backDesc: '删除', frontCode: 'delete', frontName: '删除' },
            ],
            children: [],
          },
        ],
      },
      {
        id: null,
        createDate: null,
        updateDate: null,
        del: false,
        frontPath: '/products/CustomTemplate',
        frontTitle: '模板管理',
        frontRelationPermission: null,
        level: 2,
        version: 2,
        publishDate: 1785486238533,
        permissionList: [
          { backCode: 'PERMISSION_PRODUCT_CUSTOM_TEMPLATE.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
          { backCode: 'PERMISSION_PRODUCT_CUSTOM_TEMPLATE.EDIT', backDesc: '编辑', frontCode: 'edit', frontName: '编辑' },
          { backCode: 'PERMISSION_PRODUCT_CUSTOM_TEMPLATE.DELETE', backDesc: '删除', frontCode: 'delete', frontName: '删除' },
          { backCode: 'PERMISSION_PRODUCT_CUSTOM_TEMPLATE.PUBLISH', backDesc: '设为发布/草稿', frontCode: 'publish', frontName: '设为发布/草稿' },
        ],
        children: [],
      },
      {
        id: null,
        createDate: null,
        updateDate: null,
        del: false,
        frontPath: '/products/categoryAttributes',
        frontTitle: '类目属性',
        frontRelationPermission: null,
        level: 2,
        version: 2,
        publishDate: 1785486238533,
        permissionList: [
          { backCode: 'PERMISSION_PRODUCT_CATEGORY_ATTRIBUTE.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
          { backCode: 'PERMISSION_PRODUCT_CATEGORY_ATTRIBUTE.EDIT', backDesc: '编辑', frontCode: 'edit', frontName: '编辑' },
          { backCode: 'PERMISSION_PRODUCT_CATEGORY_ATTRIBUTE.DELETE', backDesc: '删除', frontCode: 'delete', frontName: '删除' },
          { backCode: 'PERMISSION_PRODUCT_CATEGORY_ATTRIBUTE.VALUE_MANAGE', backDesc: '值管理', frontCode: 'valueManage', frontName: '值管理' },
        ],
        children: [
          {
            id: null,
            createDate: null,
            updateDate: null,
            del: false,
            frontPath: '/products/categoryAttributes/valueAttribute',
            frontTitle: '类目属性-值管理',
            frontRelationPermission: 'valueManage',
            level: 3,
            version: 2,
            publishDate: 1785486238533,
            permissionList: [
              { backCode: 'PERMISSION_PRODUCT_CATEGORY_ATTRIBUTE_VALUE.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
              { backCode: 'PERMISSION_PRODUCT_CATEGORY_ATTRIBUTE_VALUE.EDIT', backDesc: '编辑', frontCode: 'edit', frontName: '编辑' },
              { backCode: 'PERMISSION_PRODUCT_CATEGORY_ATTRIBUTE_VALUE.DELETE', backDesc: '删除', frontCode: 'delete', frontName: '删除' },
            ],
            children: [],
          },
        ],
      },
      {
        id: null,
        createDate: null,
        updateDate: null,
        del: false,
        frontPath: '/products/collection',
        frontTitle: 'Collection',
        frontRelationPermission: null,
        level: 2,
        version: 2,
        publishDate: 1785486238533,
        permissionList: [
          { backCode: 'PERMISSION_PRODUCT_COLLECTION.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
          { backCode: 'PERMISSION_PRODUCT_COLLECTION.EDIT', backDesc: '编辑', frontCode: 'edit', frontName: '编辑' },
          { backCode: 'PERMISSION_PRODUCT_COLLECTION.DELETE', backDesc: '删除', frontCode: 'delete', frontName: '删除' },
          { backCode: 'PERMISSION_PRODUCT_COLLECTION.RELATE_PRODUCT', backDesc: '关联商品', frontCode: 'relateProduct', frontName: '关联商品' },
        ],
        children: [
          {
            id: null,
            createDate: null,
            updateDate: null,
            del: false,
            frontPath: '/collection/products/relateProduct',
            frontTitle: 'Collection-关联商品',
            frontRelationPermission: 'relateProduct',
            level: 3,
            version: 2,
            publishDate: 1785486238533,
            permissionList: [
              { backCode: 'PERMISSION_PRODUCT_COLLECTION_RELATE.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
              { backCode: 'PERMISSION_PRODUCT_COLLECTION_RELATE.EDIT', backDesc: '编辑', frontCode: 'edit', frontName: '编辑' },
              { backCode: 'PERMISSION_PRODUCT_COLLECTION_RELATE.DELETE', backDesc: '删除', frontCode: 'delete', frontName: '删除' },
              { backCode: 'PERMISSION_PRODUCT_COLLECTION_RELATE.SORT', backDesc: '排序', frontCode: 'sort', frontName: '排序' },
            ],
            children: [],
          },
        ],
      },
    ],
  },
  {
    id: '2083106383137595395',
    createDate: 1785486238642,
    updateDate: 1785486238642,
    del: false,
    frontPath: '/contents',
    frontTitle: '内容管理',
    frontRelationPermission: null,
    level: 1,
    version: 2,
    publishDate: 1785486238533,
    permissionList: [],
    children: [
      {
        id: null,
        createDate: null,
        updateDate: null,
        del: false,
        frontPath: '/contents/topic',
        frontTitle: '专题',
        frontRelationPermission: null,
        level: 2,
        version: 2,
        publishDate: 1785486238533,
        permissionList: [
          { backCode: 'PERMISSION_CONTENT_TOPIC.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
          { backCode: 'PERMISSION_CONTENT_TOPIC.EDIT', backDesc: '编辑', frontCode: 'edit', frontName: '编辑' },
          { backCode: 'PERMISSION_CONTENT_TOPIC.DELETE', backDesc: '删除', frontCode: 'delete', frontName: '删除' },
        ],
        children: [],
      },
      {
        id: null,
        createDate: null,
        updateDate: null,
        del: false,
        frontPath: '/contents/posts',
        frontTitle: '文章',
        frontRelationPermission: null,
        level: 2,
        version: 2,
        publishDate: 1785486238533,
        permissionList: [
          { backCode: 'PERMISSION_CONTENT_POST.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
          { backCode: 'PERMISSION_CONTENT_POST.EDIT', backDesc: '编辑（含修改专题）', frontCode: 'edit', frontName: '编辑（含修改专题）' },
          { backCode: 'PERMISSION_CONTENT_POST.DELETE', backDesc: '删除', frontCode: 'delete', frontName: '删除' },
          { backCode: 'PERMISSION_CONTENT_POST.PUBLISH', backDesc: '发布', frontCode: 'publish', frontName: '发布' },
          { backCode: 'PERMISSION_CONTENT_POST.RELATE_PRODUCT', backDesc: '关联商品', frontCode: 'relateProduct', frontName: '关联商品' },
        ],
        children: [
          {
            id: null,
            createDate: null,
            updateDate: null,
            del: false,
            frontPath: '/contents/products/relateProduct',
            frontTitle: '文章-关联商品',
            frontRelationPermission: 'relateProduct',
            level: 3,
            version: 2,
            publishDate: 1785486238533,
            permissionList: [
              { backCode: 'PERMISSION_CONTENT_POST_RELATE.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
              { backCode: 'PERMISSION_CONTENT_POST_RELATE.EDIT', backDesc: '编辑', frontCode: 'edit', frontName: '编辑' },
              { backCode: 'PERMISSION_CONTENT_POST_RELATE.DELETE', backDesc: '删除', frontCode: 'delete', frontName: '删除' },
            ],
            children: [],
          },
        ],
      },
    ],
  },
  {
    id: '2083106383137595396',
    createDate: 1785486238642,
    updateDate: 1785486238642,
    del: false,
    frontPath: '/mallConfig',
    frontTitle: '商城配置',
    frontRelationPermission: null,
    level: 1,
    version: 2,
    publishDate: 1785486238533,
    permissionList: [],
    children: [
      {
        id: null,
        createDate: null,
        updateDate: null,
        del: false,
        frontPath: '/mallConfig/home-list',
        frontTitle: '首页配置',
        frontRelationPermission: null,
        level: 2,
        version: 2,
        publishDate: 1785486238533,
        permissionList: [
          { backCode: 'PERMISSION_MALL_HOME.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
          { backCode: 'PERMISSION_MALL_HOME.EDIT', backDesc: '编辑（显示设置、排序、置顶）', frontCode: 'edit', frontName: '编辑（显示设置、排序、置顶）' },
          { backCode: 'PERMISSION_MALL_HOME.DELETE', backDesc: '删除', frontCode: 'delete', frontName: '删除' },
          { backCode: 'PERMISSION_MALL_HOME.PUBLISH', backDesc: '发布', frontCode: 'publish', frontName: '发布' },
        ],
        children: [],
      },
      {
        id: null,
        createDate: null,
        updateDate: null,
        del: false,
        frontPath: '/mallConfig/guidingstar',
        frontTitle: '网站导航',
        frontRelationPermission: null,
        level: 2,
        version: 2,
        publishDate: 1785486238533,
        permissionList: [
          { backCode: 'PERMISSION_MALL_NAVIGATION.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
          { backCode: 'PERMISSION_MALL_NAVIGATION.EDIT', backDesc: '编辑（显示/隐藏、拖拽排序）', frontCode: 'edit', frontName: '编辑（显示/隐藏、拖拽排序）' },
          { backCode: 'PERMISSION_MALL_NAVIGATION.DELETE', backDesc: '删除', frontCode: 'delete', frontName: '删除' },
          { backCode: 'PERMISSION_MALL_NAVIGATION.PUBLISH', backDesc: '发布', frontCode: 'publish', frontName: '发布' },
          { backCode: 'PERMISSION_MALL_NAVIGATION.ADD_SUB_DIR', backDesc: '添加目录', frontCode: 'addSubDir', frontName: '添加目录' },
        ],
        children: [],
      },
    ],
  },
  {
    id: '2083106383137595397',
    createDate: 1785486238642,
    updateDate: 1785486238642,
    del: false,
    frontPath: '/usercenter',
    frontTitle: '会员管理',
    frontRelationPermission: null,
    level: 1,
    version: 2,
    publishDate: 1785486238533,
    permissionList: [],
    children: [
      {
        id: null,
        createDate: null,
        updateDate: null,
        del: false,
        frontPath: '/usercenter/user-list',
        frontTitle: '用户管理',
        frontRelationPermission: null,
        level: 2,
        version: 2,
        publishDate: 1785486238533,
        permissionList: [
          { backCode: 'PERMISSION_MEMBER_USER.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
          { backCode: 'PERMISSION_MEMBER_USER.DETAIL', backDesc: '详情', frontCode: 'detail', frontName: '详情' },
          { backCode: 'PERMISSION_MEMBER_USER.EXPORT', backDesc: '导出', frontCode: 'export', frontName: '导出' },
        ],
        children: [],
      },
      {
        id: null,
        createDate: null,
        updateDate: null,
        del: false,
        frontPath: '/usercenter/contact-list',
        frontTitle: '联系人列表',
        frontRelationPermission: null,
        level: 2,
        version: 2,
        publishDate: 1785486238533,
        permissionList: [
          { backCode: 'PERMISSION_MEMBER_CONTACT.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
          { backCode: 'PERMISSION_MEMBER_CONTACT.DETAIL', backDesc: '详情', frontCode: 'detail', frontName: '详情' },
        ],
        children: [],
      },
    ],
  },
  {
    id: '2083106383137595398',
    createDate: 1785486238642,
    updateDate: 1785486238642,
    del: false,
    frontPath: '/supplyChain',
    frontTitle: '供应链',
    frontRelationPermission: null,
    level: 1,
    version: 2,
    publishDate: 1785486238533,
    permissionList: [],
    children: [
      {
        id: null,
        createDate: null,
        updateDate: null,
        del: false,
        frontPath: '/supplyChain/factory',
        frontTitle: '工厂管理',
        frontRelationPermission: null,
        level: 2,
        version: 2,
        publishDate: 1785486238533,
        permissionList: [
          { backCode: 'PERMISSION_SUPPLY_CHAIN_FACTORY.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
          { backCode: 'PERMISSION_SUPPLY_CHAIN_FACTORY.DETAIL', backDesc: '详情', frontCode: 'detail', frontName: '详情' },
          { backCode: 'PERMISSION_SUPPLY_CHAIN_FACTORY.EDIT', backDesc: '编辑', frontCode: 'edit', frontName: '编辑' },
          { backCode: 'PERMISSION_SUPPLY_CHAIN_FACTORY.DELETE', backDesc: '删除', frontCode: 'delete', frontName: '删除' },
          { backCode: 'PERMISSION_SUPPLY_CHAIN_FACTORY.ENABLE', backDesc: '启用/停用', frontCode: 'enable', frontName: '启用/停用' },
          { backCode: 'PERMISSION_SUPPLY_CHAIN_FACTORY.IMPORT', backDesc: '导入', frontCode: 'import', frontName: '导入' },
        ],
        children: [],
      },
    ],
  },
  {
    id: '2083106383137595399',
    createDate: 1785486238642,
    updateDate: 1785486238642,
    del: false,
    frontPath: '/accountCenter',
    frontTitle: '用户中心',
    frontRelationPermission: null,
    level: 1,
    version: 2,
    publishDate: 1785486238533,
    permissionList: [],
    children: [
      {
        id: null,
        createDate: null,
        updateDate: null,
        del: false,
        frontPath: '/accountCenter/account',
        frontTitle: '用户管理',
        frontRelationPermission: null,
        level: 2,
        version: 2,
        publishDate: 1785486238533,
        permissionList: [
          { backCode: 'PERMISSION_ACCOUNT_USER.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
          { backCode: 'PERMISSION_ACCOUNT_USER.DETAIL', backDesc: '详情', frontCode: 'detail', frontName: '详情' },
          { backCode: 'PERMISSION_ACCOUNT_USER.EDIT', backDesc: '编辑', frontCode: 'edit', frontName: '编辑' },
          { backCode: 'PERMISSION_ACCOUNT_USER.RESET_PASSWORD', backDesc: '重置密码', frontCode: 'resetPassword', frontName: '重置密码' },
          { backCode: 'PERMISSION_ACCOUNT_USER.ENABLE', backDesc: '启用/停用', frontCode: 'enable', frontName: '启用/停用' },
        ],
        children: [],
      },
      {
        id: null,
        createDate: null,
        updateDate: null,
        del: false,
        frontPath: '/accountCenter/role',
        frontTitle: '角色管理',
        frontRelationPermission: null,
        level: 2,
        version: 2,
        publishDate: 1785486238533,
        permissionList: [
          { backCode: 'PERMISSION_ACCOUNT_ROLE.READ', backDesc: '查看', frontCode: 'read', frontName: '查看' },
          { backCode: 'PERMISSION_ACCOUNT_ROLE.DETAIL', backDesc: '详情', frontCode: 'detail', frontName: '详情' },
          { backCode: 'PERMISSION_ACCOUNT_ROLE.EDIT', backDesc: '编辑', frontCode: 'edit', frontName: '编辑' },
          { backCode: 'PERMISSION_ACCOUNT_ROLE.DELETE', backDesc: '删除', frontCode: 'delete', frontName: '删除' },
          { backCode: 'PERMISSION_ACCOUNT_ROLE.ENABLE', backDesc: '启用/停用', frontCode: 'enable', frontName: '启用/停用' },
        ],
        children: [],
      },
    ],
  },
];

/**
 * 把前端权限树映射成 filterRoutesByFunctionList 所需的 FunctionListModule[]：
 *   frontPath  → path
 *   permissionList[].frontCode → permissionList[].code
 * 这样现有权限过滤逻辑零改动即可接入前端权限树。
 */
export const mapFrontTreeToFunctionList = (
  tree: IFrontPermissionNode[] = FrontPermissionTree,
): FunctionListModule[] =>
  tree.map((module) => ({
    path: module.frontPath,
    children: module.children.map((child) => ({
      path: child.frontPath,
      permissionList: child.permissionList.map((p) => ({ code: p.frontCode })),
    })),
  }));

const universalLayoutRotes: IRouter[] = [
  /*
  {
    path: '/home',
    meta: {
      icon: 'home',
      title: 'universal-layout.menu.home',
    },
    component: lazy(() => import('@/pages/Home')),
  },
  */
  {
    path: '/home',
    meta: {
      icon: 'home',
      title: '首页',
      keepAlive: false,
    },
    component: lazy(() => import('@/pages/GeneralTradeLogistics/Cockpit')),
  },
  {
    path: '/cockpit',
    meta: { hidden: true, keepAlive: false, title: '首页' },
    component: lazy(() => import('@/pages/GeneralTradeLogistics/Cockpit')),
  },
  {
    path: '/cockpit/overview',
    meta: { hidden: true, keepAlive: false, title: '首页' },
    component: lazy(() => import('@/pages/GeneralTradeLogistics/Cockpit')),
  },
  {
    path: '/generalTradeLogistics',
    meta: {
      icon: 'control',
      title: '头程竞价管理',
    },
    redirect: '/generalTradeLogistics/freightInquiry',
    children: [
      {
        path: '/generalTradeLogistics/freightInquiry',
        meta: { keepAlive: false, title: '发起询价' },
        component: lazy(() => import('@/pages/GeneralTradeLogistics/FreightInquiry')),
      },
      {
        path: '/generalTradeLogistics/inquiryManagement',
        meta: { keepAlive: false, title: '询价管理' },
        component: lazy(() => import('@/pages/GeneralTradeLogistics/InquiryManagement')),
      },
      {
        path: '/generalTradeLogistics/competitionAnalysis',
        meta: { keepAlive: false, title: '竞价分析' },
        component: lazy(() => import('@/pages/GeneralTradeLogistics/CompetitionAnalysis')),
      },
      {
        path: '/generalTradeLogistics/pricingReview',
        meta: { keepAlive: false, title: '定价审核' },
        component: lazy(() => import('@/pages/GeneralTradeLogistics/PricingReview')),
      },
      {
        path: '/generalTradeLogistics/logisticsTracking',
        meta: { keepAlive: false, title: '物流轨迹跟踪' },
        component: lazy(() => import('@/pages/GeneralTradeLogistics/FeaturePlaceholder')),
      },
      {
        path: '/generalTradeLogistics/reconciliationReview',
        meta: { keepAlive: false, title: '对账与审核' },
        component: lazy(() => import('@/pages/GeneralTradeLogistics/FeaturePlaceholder')),
      },
      {
        path: '/generalTradeLogistics/exceptionPermission',
        meta: { keepAlive: false, title: '异常与权限' },
        component: lazy(() => import('@/pages/GeneralTradeLogistics/FeaturePlaceholder')),
      },
    ],
  },
  {
    path: '/generalTradeLogisticsBase',
    meta: { icon: 'set', title: '基础配置' },
    redirect: '/generalTradeLogisticsBase/providerConfig',
    children: [
      {
        path: '/generalTradeLogisticsBase/providerConfig',
        meta: { keepAlive: false, title: '物流商参数配置' },
        component: lazy(() => import('@/pages/GeneralTradeLogistics/ProviderConfig')),
      },
      {
        path: '/generalTradeLogisticsBase/systemConfig',
        meta: { keepAlive: false, title: '系统参数配置' },
        component: lazy(() => import('@/pages/GeneralTradeLogistics/SystemConfig')),
      },
      {
        path: '/generalTradeLogisticsBase/lingxingProducts',
        meta: { keepAlive: false, title: '领星产品' },
        component: lazy(() => import('@/pages/GeneralTradeLogistics/LingxingProducts')),
      },
    ],
  },
  {
    path: '/generalTradeLogistics/providerConfig',
    meta: { hidden: true, keepAlive: false, title: '物流商参数配置' },
    component: lazy(() => import('@/pages/GeneralTradeLogistics/ProviderConfig')),
  },
  {
    path: '/generalTradeLogistics/systemConfig',
    meta: { hidden: true, keepAlive: false, title: '系统参数配置' },
    component: lazy(() => import('@/pages/GeneralTradeLogistics/SystemConfig')),
  },
];

export default universalLayoutRotes;
export {
  PARENT_SECONDARY_MAP,
  FrontPermissionTree,
};
